// © Microsoft Corporation. All rights reserved.

import { AppEnvironment, appEnvironment } from "..";
import { IRequestContext, IRequestData } from "./IRequestContext";
import { IRequestDataInteropValue, IRequestDataSpecification, RequestData } from "@msnews/ssr-server";
import { SSRContract, SSRRequestDataPrefix } from "@msnews/isomorphic-util";

/**
 * Defines the parameters used to create a request contract.
 * Destructured from an object due to the combinations of parameters that are valid.
 */
export interface IRequestContextContract {
    /**
     * URL Search params that contain request data (client-side values sent to the server).
     * Only required when called server-side
     */
    params?: URLSearchParams;

    /**
     * The prefix to use to pull query params related to request data.
     */
    qspPrefix?: string;

    /**
     * The specification defining what request data should be passed between client and server.
     */
    spec?: IRequestDataSpecification;
}

/**
 * Used to propagate client-only data (like window.innerWidth) and singletons (like AppEnvironment)
 * in a non-global way to assist in making all libs/components/experiences isomorphic.
 */
export class RequestContext implements IRequestContext {
    private requestData: RequestData<IRequestData>;

    /**
     * Create a new request context.
     *
     * If spec or qspPrefix is not provided it will use the defaults specified in "@msnews/isomorphic-util"
     *
     * @param IRequestContextContract parameters used to initialize the request context
     */
    constructor({ params, qspPrefix, spec }: IRequestContextContract = {}) {
        this.requestData = new RequestData(spec || SSRContract, qspPrefix || SSRRequestDataPrefix);
        if (params) {
            this.requestData.loadQueryParams(params);
        }
    }

    /**
     * The request ID is used to distinguish requests server-side.
     * This method passes through the app environment ActivityId
     */
    public get requestId(): string {
        return this.app.ActivityId;
    }

    /**
     * The current app environment.
     */
    public get app(): AppEnvironment {
        // TODO: Remove global reference and add to the constructor parameters.
        // The constructor should create a new app environment or accept an optional override.
        return appEnvironment;
    }

    /**
     * The current request data. i.e. client-side data that is passed
     * between client and server.
     */
    public get data(): IRequestData {
        return this.requestData.data;
    }
}

/**
 * Verifies the input data is defined, and throws an error if it is not.
 * @param dataName - The name of the requestContext value that we are verifying.
 * @param data - The value being checked.
 */
export function verifyDataExists(dataName: string, data: IRequestDataInteropValue): void {
    if (data === undefined) {
        throw new Error("Expected " + dataName + " to return value. Possible invalid RequestContext spec used.");
    }
}
